import React from 'react';
import { useTranslation } from 'react-i18next';

const Approach: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="container-custom section-padding">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-mypa-blue mb-4">
          {t("approach.title")}
        </h1>
        <p className="text-xl max-w-3xl mx-auto">
          {t("approach.subtitle")}
        </p>
      </div>
      
      {/* Methodology Section */}
      <section className="mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-mypa-green mb-6">{t("approach.methodology.title")}</h2>
            <p className="text-lg mb-6">
              {t("approach.methodology.description")}
            </p>
            <p className="text-lg">
              Notre approche se distingue par sa capacité à s'adapter à votre contexte spécifique, en tenant compte de vos contraintes, de vos opportunités et de vos objectifs particuliers.
            </p>
          </div>
          <div className="bg-mypa-light p-8 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-blue mb-4">Nos principes directeurs</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Pragmatisme et réalisme dans les objectifs</span>
              </li>
              <li className="flex items-start">
                <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Approche progressive et adaptée à votre maturité</span>
              </li>
              <li className="flex items-start">
                <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Mesure précise des résultats et des impacts</span>
              </li>
              <li className="flex items-start">
                <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Intégration des spécificités locales</span>
              </li>
              <li className="flex items-start">
                <div className="bg-mypa-green rounded-full p-1 mr-3 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Accompagnement humain et technique</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
      
      {/* Process Steps Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-green mb-8 text-center">{t("approach.steps.title")}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 bg-mypa-green rounded-full flex items-center justify-center text-white font-bold text-xl">1</div>
            <h3 className="text-xl font-bold text-mypa-blue mb-3 mt-4">{t("approach.steps.diagnostic.title")}</h3>
            <p>{t("approach.steps.diagnostic.description")}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>• Collecte et analyse de données</li>
              <li>• Identification des sources d'émissions</li>
              <li>• Évaluation de la maturité</li>
              <li>• Benchmark sectoriel</li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 bg-mypa-green rounded-full flex items-center justify-center text-white font-bold text-xl">2</div>
            <h3 className="text-xl font-bold text-mypa-blue mb-3 mt-4">{t("approach.steps.strategy.title")}</h3>
            <p>{t("approach.steps.strategy.description")}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>• Définition d'objectifs réalistes</li>
              <li>• Identification des leviers d'action</li>
              <li>• Priorisation des initiatives</li>
              <li>• Élaboration du plan d'action</li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 bg-mypa-green rounded-full flex items-center justify-center text-white font-bold text-xl">3</div>
            <h3 className="text-xl font-bold text-mypa-blue mb-3 mt-4">{t("approach.steps.implementation.title")}</h3>
            <p>{t("approach.steps.implementation.description")}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>• Déploiement des actions</li>
              <li>• Formation des équipes</li>
              <li>• Mise en place des outils</li>
              <li>• Accompagnement au changement</li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md relative">
            <div className="absolute -top-4 -left-4 w-12 h-12 bg-mypa-green rounded-full flex items-center justify-center text-white font-bold text-xl">4</div>
            <h3 className="text-xl font-bold text-mypa-blue mb-3 mt-4">{t("approach.steps.monitoring.title")}</h3>
            <p>{t("approach.steps.monitoring.description")}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>• Suivi des indicateurs clés</li>
              <li>• Analyse des résultats</li>
              <li>• Ajustements et optimisations</li>
              <li>• Reporting et communication</li>
            </ul>
          </div>
        </div>
      </section>
      
      {/* Adaptation Section */}
      <section className="mb-16 bg-mypa-light p-8 rounded-lg">
        <h2 className="text-3xl font-bold text-mypa-blue mb-6">Une approche adaptée à votre contexte</h2>
        <p className="text-lg mb-6">
          Nous reconnaissons que chaque organisation est unique, avec ses propres défis, contraintes et opportunités. C'est pourquoi notre méthodologie s'adapte à votre réalité spécifique.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-green mb-3">Adaptation sectorielle</h3>
            <p>Des approches spécifiques selon votre secteur d'activité et ses enjeux particuliers.</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-green mb-3">Adaptation à la taille</h3>
            <p>Des solutions dimensionnées pour les TPE/PME comme pour les grandes entreprises.</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-green mb-3">Adaptation à la maturité</h3>
            <p>Un accompagnement progressif qui tient compte de votre niveau d'avancement.</p>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="text-center">
        <h2 className="text-3xl font-bold text-mypa-blue mb-6">Prêt à démarrer votre démarche de décarbonation ?</h2>
        <p className="text-lg mb-8 max-w-3xl mx-auto">
          Contactez-nous pour échanger sur vos enjeux spécifiques et découvrir comment notre approche peut s'adapter à votre contexte.
        </p>
        <a href="/contact" className="btn-primary">
          Nous contacter
        </a>
      </section>
    </div>
  );
};

export default Approach;
